import { createClient } from "@supabase/supabase-js";
import type { User as AuthUser } from "@supabase/supabase-js";
import type { Car, CarOptionSet, FuelLog, GasStation, GasStationBrand, ServiceHistory, ShftService, User } from "@/types/database";
import type {
  ApprovalStatus,
  Business,
  ConnectedService,
  Customer,
  ShftServicePrice,
} from "@/types/database";
import { buildCarMakeLogoLookup, type CarMakeLogoEntry } from "@/lib/car-make-logos";
import { uploadGasStationBrandLogo, uploadGasStationLogo } from "@/lib/gas-station-storage";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn("Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY");
}

export const supabase = createClient(supabaseUrl ?? "", supabaseAnonKey ?? "", {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

export function hasAdminMetadata(user: AuthUser): boolean {
  const meta = user.app_metadata ?? {};
  return meta.is_admin === true || meta.role === "admin" || meta.overwatch_admin === true;
}

const USER_FIELDS =
  "id,created_at,firstName,lastName,country,userID,email,profilePhoto,DOB,phoneNumber,address,tokenAmount,vinLookupAmount,is_admin,carID";

export async function fetchAdminUserByEmail(email: string): Promise<User | null> {
  const { data, error } = await supabase
    .from("Users")
    .select(USER_FIELDS)
    .eq("email", email)
    .eq("is_admin", true)
    .maybeSingle();

  if (error) throw error;
  return (data ?? null) as User | null;
}

export async function resolveAdminAccess(user: AuthUser): Promise<boolean> {
  if (hasAdminMetadata(user)) return true;

  const checks = await Promise.all([
    user.email
      ? supabase.from("Users").select("is_admin").eq("email", user.email).maybeSingle()
      : Promise.resolve({ data: null, error: null }),
    supabase.from("Users").select("is_admin").eq("userID", user.id).maybeSingle(),
  ]);

  for (const { data, error } of checks) {
    if (error) {
      console.warn("Admin access check failed:", error.message);
      continue;
    }
    if (data?.is_admin === true) return true;
  }

  return false;
}

export const CAR_LIST_COLUMNS =
  "id,carID,userID,make,model,modelYear,trim,series,color,transmission,drive,fuelTypePrimary,enginePowerHP,odometer,value,shiftRating,ownersRating,licensePlate,vin,carImage,carLogo,body,numberOfDoors,numberOfSeats,fuelCapacity,maxSpeed,CoOwners";

export const CAR_ANALYTICS_COLUMNS =
  "id,carID,userID,make,model,modelYear,carImage,body,transmission";

export const GAS_STATION_LIST_COLUMNS =
  "id,name,address,fuelPrice_87,fuelPrice_90,fuelPrice_Diesel,fuelPrice_ULSD,fuelType,stationLogo,parish,coordinatesLat,coordinatesLong,slug,brandID,stationID";

export async function fetchCars(): Promise<Car[]> {
  const { data, error } = await supabase
    .from("Cars")
    .select(CAR_LIST_COLUMNS)
    .order("modelYear", { ascending: false });

  if (error) throw error;
  return (data ?? []) as Car[];
}

export async function fetchCarsForAnalytics(): Promise<Car[]> {
  const { data, error } = await supabase
    .from("Cars")
    .select(CAR_ANALYTICS_COLUMNS)
    .order("modelYear", { ascending: false });

  if (error) throw error;
  return (data ?? []) as Car[];
}

export async function fetchCarById(carId: string): Promise<Car | null> {
  const { data, error } = await supabase
    .from("Cars")
    .select("*")
    .eq("carID", carId)
    .maybeSingle();

  if (error) throw error;
  return data as Car | null;
}

export async function updateCar(carId: string, updates: Partial<Car>): Promise<void> {
  const { error } = await supabase.from("Cars").update(updates).eq("carID", carId);
  if (error) throw error;
}

export async function deleteCar(carId: string): Promise<void> {
  const { error } = await supabase.from("Cars").delete().eq("carID", carId);
  if (error) throw error;
}

export async function fetchGasStations(): Promise<GasStation[]> {
  const { data, error } = await supabase
    .from("GasStation")
    .select(GAS_STATION_LIST_COLUMNS)
    .order("name", { ascending: true });

  if (error) throw error;
  return (data ?? []) as GasStation[];
}

export async function fetchGasStationById(id: number): Promise<GasStation | null> {
  const { data, error } = await supabase
    .from("GasStation")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error) throw error;
  return data as GasStation | null;
}

export async function updateGasStation(id: number, updates: Partial<GasStation>): Promise<GasStation> {
  const { data, error } = await supabase
    .from("GasStation")
    .update(updates)
    .eq("id", id)
    .select(GAS_STATION_LIST_COLUMNS)
    .maybeSingle();

  if (error) throw error;
  if (!data) {
    throw new Error("Update failed. The station may be missing or you may not have permission to edit it.");
  }
  return data as GasStation;
}

export async function createGasStation(input: {
  name: string;
  address: string;
  brandID: string;
  parish?: string | null;
  fuelType?: string | null;
  slug?: string | null;
  fuelPrice_87?: number | null;
  fuelPrice_90?: number | null;
  fuelPrice_Diesel?: number | null;
  fuelPrice_ULSD?: number | null;
  coordinatesLat?: number | null;
  coordinatesLong?: number | null;
  stationLogo?: string | null;
}): Promise<GasStation> {
  const { data, error } = await supabase
    .from("GasStation")
    .insert({
      name: input.name,
      address: input.address,
      brandID: input.brandID,
      parish: input.parish ?? null,
      fuelType: input.fuelType ?? null,
      slug: input.slug ?? null,
      fuelPrice_87: input.fuelPrice_87 ?? null,
      fuelPrice_90: input.fuelPrice_90 ?? null,
      fuelPrice_Diesel: input.fuelPrice_Diesel ?? null,
      fuelPrice_ULSD: input.fuelPrice_ULSD ?? null,
      coordinatesLat: input.coordinatesLat ?? null,
      coordinatesLong: input.coordinatesLong ?? null,
      stationLogo: input.stationLogo ?? null,
    })
    .select(GAS_STATION_LIST_COLUMNS);

  if (error) throw error;
  if (!data?.[0]) {
    throw new Error("Create failed. You may not have permission to add gas stations.");
  }
  return data[0] as GasStation;
}

export async function createGasStationWithLogo(input: {
  name: string;
  address: string;
  brandID: string;
  parish?: string | null;
  fuelType?: string | null;
  slug?: string | null;
  fuelPrice_87?: number | null;
  fuelPrice_90?: number | null;
  fuelPrice_Diesel?: number | null;
  fuelPrice_ULSD?: number | null;
  coordinatesLat?: number | null;
  coordinatesLong?: number | null;
  logoFile?: File | null;
}): Promise<GasStation> {
  const brand = await fetchGasStationBrandById(input.brandID);
  const brandLogo = brand?.brandLogoUrl ?? null;

  const station = await createGasStation({
    name: input.name,
    address: input.address,
    brandID: input.brandID,
    parish: input.parish,
    fuelType: input.fuelType,
    slug: input.slug,
    fuelPrice_87: input.fuelPrice_87,
    fuelPrice_90: input.fuelPrice_90,
    fuelPrice_Diesel: input.fuelPrice_Diesel,
    fuelPrice_ULSD: input.fuelPrice_ULSD,
    coordinatesLat: input.coordinatesLat,
    coordinatesLong: input.coordinatesLong,
    stationLogo: brandLogo,
  });

  if (!input.logoFile) return station;

  const stationLogo = await uploadGasStationLogo(station.id, input.logoFile);
  return updateGasStation(station.id, { stationLogo });
}

export async function updateGasStationWithLogo(
  id: number,
  input: {
    updates: Partial<GasStation>;
    logoFile?: File | null;
    stationLogo?: string | null;
  }
): Promise<void> {
  let stationLogo = input.stationLogo ?? input.updates.stationLogo ?? null;

  if (input.logoFile) {
    stationLogo = await uploadGasStationLogo(id, input.logoFile);
  }

  await updateGasStation(id, { ...input.updates, stationLogo });
}

export async function deleteGasStation(id: number): Promise<void> {
  const { data, error } = await supabase.from("GasStation").delete().eq("id", id).select("id").maybeSingle();

  if (error) throw error;
  if (!data) {
    throw new Error("Delete failed. The station may be missing or you may not have permission to delete it.");
  }
}

export const GAS_STATION_BRAND_COLUMNS =
  "id,created_at,brandName,brandLogoUrl,description,website,phone,country";

export async function fetchGasStationBrands(): Promise<GasStationBrand[]> {
  const { data, error } = await supabase
    .from("GasStationBrand")
    .select(GAS_STATION_BRAND_COLUMNS)
    .order("brandName", { ascending: true });

  if (error) throw error;
  return (data ?? []) as GasStationBrand[];
}

export async function fetchGasStationBrandById(id: string): Promise<GasStationBrand | null> {
  const { data, error } = await supabase
    .from("GasStationBrand")
    .select(GAS_STATION_BRAND_COLUMNS)
    .eq("id", id)
    .maybeSingle();

  if (error) throw error;
  return (data ?? null) as GasStationBrand | null;
}

export async function createGasStationBrand(input: {
  id?: string;
  brandName: string;
  brandLogoUrl?: string | null;
  description?: string | null;
  website?: string | null;
  phone?: number | null;
  country?: string | null;
}): Promise<GasStationBrand> {
  const { data, error } = await supabase
    .from("GasStationBrand")
    .insert({
      ...(input.id ? { id: input.id } : {}),
      brandName: input.brandName,
      brandLogoUrl: input.brandLogoUrl ?? null,
      description: input.description ?? null,
      website: input.website ?? null,
      phone: input.phone ?? null,
      country: input.country ?? null,
    })
    .select(GAS_STATION_BRAND_COLUMNS);

  if (error) throw error;
  if (!data?.[0]) {
    throw new Error("Create failed. You may not have permission to add gas station brands.");
  }
  return data[0] as GasStationBrand;
}

export async function createGasStationBrandWithLogo(input: {
  brandName: string;
  description?: string | null;
  website?: string | null;
  phone?: number | null;
  country?: string | null;
  logoFile: File;
}): Promise<GasStationBrand> {
  const id = crypto.randomUUID();
  const brandLogoUrl = await uploadGasStationBrandLogo(id, input.logoFile);

  return createGasStationBrand({
    id,
    brandName: input.brandName,
    brandLogoUrl,
    description: input.description,
    website: input.website,
    phone: input.phone,
    country: input.country,
  });
}

export async function updateGasStationBrand(
  id: string,
  input: {
    brandName: string;
    brandLogoUrl?: string | null;
    description?: string | null;
    website?: string | null;
    phone?: number | null;
    country?: string | null;
  }
): Promise<GasStationBrand> {
  const { data, error } = await supabase
    .from("GasStationBrand")
    .update({
      brandName: input.brandName,
      brandLogoUrl: input.brandLogoUrl ?? null,
      description: input.description ?? null,
      website: input.website ?? null,
      phone: input.phone ?? null,
      country: input.country ?? null,
    })
    .eq("id", id)
    .select(GAS_STATION_BRAND_COLUMNS);

  if (error) throw error;
  if (!data?.[0]) {
    throw new Error("Update failed. The brand may be missing or you may not have permission to edit it.");
  }
  return data[0] as GasStationBrand;
}

export async function updateGasStationBrandWithLogo(
  id: string,
  input: {
    brandName: string;
    description?: string | null;
    website?: string | null;
    phone?: number | null;
    country?: string | null;
    logoFile?: File | null;
    brandLogoUrl?: string | null;
  }
): Promise<GasStationBrand> {
  let brandLogoUrl = input.brandLogoUrl ?? null;

  if (input.logoFile) {
    brandLogoUrl = await uploadGasStationBrandLogo(id, input.logoFile);
  }

  return updateGasStationBrand(id, {
    brandName: input.brandName,
    brandLogoUrl,
    description: input.description,
    website: input.website,
    phone: input.phone,
    country: input.country,
  });
}

export async function deleteGasStationBrand(id: string): Promise<void> {
  const { data, error } = await supabase.from("GasStationBrand").delete().eq("id", id).select("id").maybeSingle();

  if (error) throw error;
  if (!data) {
    throw new Error("Delete failed. The brand may be missing or you may not have permission to delete it.");
  }
}

export async function fetchUserByUserId(userID: string): Promise<User | null> {
  const { data, error } = await supabase
    .from("Users")
    .select(USER_FIELDS)
    .eq("userID", userID)
    .maybeSingle();

  if (error) throw error;
  return (data ?? null) as User | null;
}

export async function fetchUsers(): Promise<User[]> {
  const { data, error } = await supabase
    .from("Users")
    .select(USER_FIELDS)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []) as User[];
}

export async function fetchUserCount(): Promise<number> {
  const { count, error } = await supabase
    .from("Users")
    .select("id", { count: "exact", head: true });

  if (error) throw error;
  return count ?? 0;
}

export async function fetchUserById(id: number): Promise<User | null> {
  const { data, error } = await supabase
    .from("Users")
    .select(USER_FIELDS)
    .eq("id", id)
    .maybeSingle();

  if (error) throw error;
  return (data ?? null) as User | null;
}

export async function fetchCarsByUserId(userID: string): Promise<Car[]> {
  const { data, error } = await supabase
    .from("Cars")
    .select(CAR_LIST_COLUMNS)
    .eq("userID", userID)
    .order("modelYear", { ascending: false });

  if (error) throw error;
  return (data ?? []) as Car[];
}

export const SERVICE_HISTORY_COLUMNS =
  "id,created_at,carID,serviceDate,mileage,serviceType,serviceDetails,serviceList,labourCost,totalCost,servicedBy,serviceProviderID,currency";

export const SHFT_SERVICE_COLUMNS =
  "id,created_at,carID,businessID,serviceID,serviceType,serviceName,cost,scheduledDate,servicesIncluded,serviceStatus,isActive";

export async function fetchServiceHistory(): Promise<ServiceHistory[]> {
  const { data, error } = await supabase
    .from("ServiceHistory")
    .select(SERVICE_HISTORY_COLUMNS)
    .order("serviceDate", { ascending: false });

  if (error) throw error;
  return (data ?? []) as ServiceHistory[];
}

export async function fetchShftServices(): Promise<ShftService[]> {
  const { data, error } = await supabase
    .from("ShftService")
    .select(SHFT_SERVICE_COLUMNS)
    .order("scheduledDate", { ascending: false });

  if (error) throw error;
  return (data ?? []) as ShftService[];
}

export async function fetchServiceHistoryForCars(carIds: string[]): Promise<ServiceHistory[]> {
  if (carIds.length === 0) return [];
  const { data, error } = await supabase
    .from("ServiceHistory")
    .select(SERVICE_HISTORY_COLUMNS)
    .in("carID", carIds)
    .order("serviceDate", { ascending: false });

  if (error) throw error;
  return (data ?? []) as ServiceHistory[];
}

export async function fetchShftServicesForCars(carIds: string[]): Promise<ShftService[]> {
  if (carIds.length === 0) return [];
  const { data, error } = await supabase
    .from("ShftService")
    .select(SHFT_SERVICE_COLUMNS)
    .in("carID", carIds)
    .order("scheduledDate", { ascending: false });

  if (error) throw error;
  return (data ?? []) as ShftService[];
}

export async function fetchCarOptionSetSummary(): Promise<Pick<CarOptionSet, "make" | "models">[]> {
  const { data, error } = await supabase
    .from("CarOptionSet")
    .select("make,models")
    .order("make", { ascending: true });

  if (error) throw error;
  return (data ?? []) as Pick<CarOptionSet, "make" | "models">[];
}

export async function fetchCarMakeLogos(): Promise<Map<string, CarMakeLogoEntry>> {
  const { data, error } = await supabase
    .from("CarLogos")
    .select("name,slug,optimized,thumb")
    .range(0, 999);

  if (error) throw error;
  return buildCarMakeLogoLookup(data ?? []);
}

export async function fetchCarOptionSets(): Promise<CarOptionSet[]> {
  const { data, error } = await supabase
    .from("CarOptionSet")
    .select("id,make,models,trims")
    .order("make", { ascending: true });

  if (error) throw error;
  return (data ?? []) as CarOptionSet[];
}

export async function createCarOptionSet(input: {
  make: string;
  models: string[];
  trims: string[];
}): Promise<CarOptionSet> {
  const { data, error } = await supabase
    .from("CarOptionSet")
    .insert({
      id: crypto.randomUUID(),
      make: input.make,
      models: input.models,
      trims: input.trims,
    })
    .select("id,make,models,trims");

  if (error) throw error;
  if (!data?.[0]) {
    throw new Error("Create failed. You may not have permission to add option sets.");
  }
  return data[0] as CarOptionSet;
}

export async function fetchCarOptionSetById(id: string): Promise<CarOptionSet | null> {
  const { data, error } = await supabase
    .from("CarOptionSet")
    .select("id,make,models,trims")
    .eq("id", id)
    .maybeSingle();

  if (error) throw error;
  return (data ?? null) as CarOptionSet | null;
}

export async function updateCarOptionSet(
  id: string,
  input: Pick<CarOptionSet, "make" | "models" | "trims">
): Promise<CarOptionSet> {
  const { data, error } = await supabase
    .from("CarOptionSet")
    .update({
      make: input.make,
      models: input.models,
      trims: input.trims,
    })
    .eq("id", id)
    .select("id,make,models,trims");

  if (error) throw error;
  if (!data?.[0]) {
    throw new Error("Update failed. The option set may be missing or you may not have permission to edit it.");
  }
  return data[0] as CarOptionSet;
}

export const FUEL_LOG_COLUMNS =
  "id,created_at,loggedAt,userID,carID,gasStationID,fuelGrade,litres,totalCost,pricePerLitre,inputMode,odometer,currency,notes";

export async function fetchFuelLogs(): Promise<FuelLog[]> {
  const { data, error } = await supabase
    .from("FuelLogs")
    .select(FUEL_LOG_COLUMNS)
    .order("loggedAt", { ascending: false });

  if (error) throw error;
  return (data ?? []) as FuelLog[];
}

export async function fetchFuelLogsByUserId(userID: string): Promise<FuelLog[]> {
  const { data, error } = await supabase
    .from("FuelLogs")
    .select(FUEL_LOG_COLUMNS)
    .eq("userID", userID)
    .order("loggedAt", { ascending: false });

  if (error) throw error;
  return (data ?? []) as FuelLog[];
}

export const BUSINESS_COLUMNS =
  "id,created_at,name,email,address,phone,country,type,users,logo,customers,businessID,slug,isShftOriginal,approvalStatus,reviewedBy,reviewedAt,rejectionReason";

export const CONNECTED_SERVICE_COLUMNS =
  "id,created_at,businessID,carID,marketingImage,promotionalText,isActive,businessLogo,businessName,businessType,approvalStatus,reviewedBy,reviewedAt,rejectionReason";

export const SHFT_SERVICE_PRICE_COLUMNS =
  "shftpriceID,businessID,connectedServiceID,currency,description,images,isAvailable,Price,pricingUnit,serviceName,specification";

export const CUSTOMER_COLUMNS =
  "id,created_at,customerID,businessID,totalSpend,firstName,lastName,country,email";

export async function fetchBusinesses(): Promise<Business[]> {
  const { data, error } = await supabase
    .from("Business")
    .select(BUSINESS_COLUMNS)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []) as Business[];
}

export async function fetchBusinessById(id: number): Promise<Business | null> {
  const { data, error } = await supabase
    .from("Business")
    .select(BUSINESS_COLUMNS)
    .eq("id", id)
    .maybeSingle();

  if (error) throw error;
  return (data ?? null) as Business | null;
}

export async function fetchConnectedServices(): Promise<ConnectedService[]> {
  const { data, error } = await supabase
    .from("ConnectedService")
    .select(CONNECTED_SERVICE_COLUMNS)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []) as ConnectedService[];
}

export async function fetchConnectedServiceByBusinessId(
  businessID: string
): Promise<ConnectedService | null> {
  if (!businessID) return null;

  const { data, error } = await supabase
    .from("ConnectedService")
    .select(CONNECTED_SERVICE_COLUMNS)
    .eq("businessID", businessID)
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return (data ?? null) as ConnectedService | null;
}

export async function fetchServicePricesByBusiness(businessID: string): Promise<ShftServicePrice[]> {
  if (!businessID) return [];

  const { data, error } = await supabase
    .from("ShftServicePrices")
    .select(SHFT_SERVICE_PRICE_COLUMNS)
    .eq("businessID", businessID)
    .order("serviceName", { ascending: true });

  if (error) throw error;
  return (data ?? []) as ShftServicePrice[];
}

export async function fetchCustomersByBusiness(businessID: string): Promise<Customer[]> {
  if (!businessID) return [];

  const { data, error } = await supabase
    .from("Customers")
    .select(CUSTOMER_COLUMNS)
    .eq("businessID", businessID)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []) as Customer[];
}

export async function fetchCustomers(): Promise<Customer[]> {
  const { data, error } = await supabase
    .from("Customers")
    .select(CUSTOMER_COLUMNS)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []) as Customer[];
}

export async function fetchCarsByCarIds(carIds: string[]): Promise<Car[]> {
  if (carIds.length === 0) return [];

  const { data, error } = await supabase
    .from("Cars")
    .select(CAR_LIST_COLUMNS)
    .in("carID", carIds)
    .order("modelYear", { ascending: false });

  if (error) throw error;
  return (data ?? []) as Car[];
}

export async function approveConnectedService(
  id: number,
  reviewedBy: string
): Promise<ConnectedService> {
  const { data, error } = await supabase
    .from("ConnectedService")
    .update({
      isActive: true,
      approvalStatus: "approved" satisfies ApprovalStatus,
      reviewedBy,
      reviewedAt: new Date().toISOString(),
      rejectionReason: null,
    })
    .eq("id", id)
    .select(CONNECTED_SERVICE_COLUMNS)
    .maybeSingle();

  if (error) throw error;
  if (!data) {
    throw new Error("Approve failed. The connected service may be missing or you may not have permission.");
  }
  return data as ConnectedService;
}

export async function rejectConnectedService(
  id: number,
  reviewedBy: string,
  rejectionReason: string
): Promise<ConnectedService> {
  const { data, error } = await supabase
    .from("ConnectedService")
    .update({
      isActive: false,
      approvalStatus: "rejected" satisfies ApprovalStatus,
      reviewedBy,
      reviewedAt: new Date().toISOString(),
      rejectionReason: rejectionReason.trim() || null,
    })
    .eq("id", id)
    .select(CONNECTED_SERVICE_COLUMNS)
    .maybeSingle();

  if (error) throw error;
  if (!data) {
    throw new Error("Reject failed. The connected service may be missing or you may not have permission.");
  }
  return data as ConnectedService;
}

export async function updateBusinessApproval(
  id: number,
  input: {
    approvalStatus: ApprovalStatus;
    reviewedBy: string;
    rejectionReason?: string | null;
  }
): Promise<Business> {
  const { data, error } = await supabase
    .from("Business")
    .update({
      approvalStatus: input.approvalStatus,
      reviewedBy: input.reviewedBy,
      reviewedAt: new Date().toISOString(),
      rejectionReason:
        input.approvalStatus === "rejected" ? (input.rejectionReason?.trim() || null) : null,
    })
    .eq("id", id)
    .select(BUSINESS_COLUMNS)
    .maybeSingle();

  if (error) throw error;
  if (!data) {
    throw new Error("Business approval update failed. The business may be missing or you may not have permission.");
  }
  return data as Business;
}

export async function setBusinessShftOriginal(
  id: number,
  isShftOriginal: boolean
): Promise<Business> {
  const { data, error } = await supabase
    .from("Business")
    .update({ isShftOriginal })
    .eq("id", id)
    .select(BUSINESS_COLUMNS)
    .maybeSingle();

  if (error) throw error;
  if (!data) {
    throw new Error(
      "Shft Original update failed. The business may be missing or you may not have permission."
    );
  }
  return data as Business;
}
